import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { InputWrapper, type BaseInputProps } from './InputWrapper';
import { optionMatcher } from './optionMatcher';
import {
  groupSelectOptions,
  type SelectOption as Option,
} from './selectOptions';
import { cn } from '~/utils';
import {
  preferredSide,
  verticalBudgets,
  type VerticalSide,
} from '~/components/ui/verticalPlacement';
import { useTranslation } from 'react-i18next';

type SearchableSelectProps = Omit<
  BaseInputProps,
  'value' | 'onChange' | 'onKeyDown'
> & {
  options: Option[];
  value?: string;
  onChange?: (name: string, value: string) => void;
  placeholder?: string;
  creatable?: boolean;
  selectedDisplay?: string;
  panelClassName?: string;
};

type PanelPlacement = ({ top: number } | { bottom: number }) & {
  left: number;
  width: number;
  maxHeight?: number;
  position: 'fixed' | 'absolute';
  container: Element;
};

const PANEL_GAP = 4;
// The search row plus one option, so a cramped panel is cut off at the edge rather than empty.
const MIN_PANEL_HEIGHT = 100;

function viewportBudgets(triggerRect: DOMRect) {
  return verticalBudgets(
    triggerRect,
    { top: 0, bottom: document.documentElement.clientHeight },
    PANEL_GAP
  );
}

export const SearchableSelect = forwardRef<
  HTMLButtonElement,
  SearchableSelectProps
>(
  (
    {
      label,
      name,
      value,
      onChange,
      options,
      disabled,
      error,
      required,
      containerClassName,
      className,
      leftIcon,
      rightIcon,
      dataCy,
      placeholder,
      onFocus,
      creatable,
      selectedDisplay,
      panelClassName,
    },
    ref
  ) => {
    const { t } = useTranslation('general');
    const id = useId();
    const errorId = `${id}-error`;
    const [open, setOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [query, setQuery] = useState('');

    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const panelSideRef = useRef<VerticalSide>('below');
    const [panel, setPanel] = useState<PanelPlacement>({
      top: 0,
      left: 0,
      width: 0,
      position: 'fixed',
      container: document.body,
    });

    useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement);

    // The panel portals out of any scrollable ancestor (e.g. the order filter dropdown) so it
    // can't get clipped. It portals into the nearest react-tiny-popover container rather than
    // document.body when there is one, because that library's own outside-click check only
    // treats clicks inside its own portal as "inside" — portaling straight to document.body
    // would make every option click close the enclosing popover. That container carries a CSS
    // transform (react-tiny-popover positions itself that way), which makes it the containing
    // block for a `position: fixed` descendant too, so the panel switches to `absolute`
    // coordinates relative to that container's own rect instead of the viewport.
    const updatePanelPosition = useCallback(() => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const triggerRect = trigger.getBoundingClientRect();
      const popoverContainer = trigger.closest('.react-tiny-popover-container');
      if (popoverContainer) {
        const containerRect = popoverContainer.getBoundingClientRect();
        setPanel({
          top: triggerRect.bottom - containerRect.top + PANEL_GAP,
          left: triggerRect.left - containerRect.left,
          width: triggerRect.width,
          position: 'absolute',
          container: popoverContainer,
        });
      } else {
        const side = panelSideRef.current;
        setPanel({
          ...(side === 'above'
            ? {
                bottom:
                  document.documentElement.clientHeight -
                  triggerRect.top +
                  PANEL_GAP,
              }
            : { top: triggerRect.bottom + PANEL_GAP }),
          left: triggerRect.left,
          width: triggerRect.width,
          maxHeight: Math.max(
            viewportBudgets(triggerRect)[side],
            MIN_PANEL_HEIGHT
          ),
          position: 'fixed',
          container: document.body,
        });
      }
    }, []);

    const { container: panelContainer, ...panelStyle } = panel;

    const selectedLabel =
      selectedDisplay ??
      options.find((o) => o.value === value)?.label ??
      value ??
      '';

    const trimmedQuery = query.trim();
    const lowerQuery = trimmedQuery.toLowerCase();
    const filtered = trimmedQuery
      ? options.filter(optionMatcher(trimmedQuery))
      : options;

    const showCreateOption =
      creatable &&
      trimmedQuery.length > 0 &&
      !options.some((o) => o.label.toLowerCase() === lowerQuery);

    const { ungrouped: ungroupedOptions, groups: groupedOptions } =
      groupSelectOptions(filtered);

    useEffect(() => {
      if (!open && !isFocused) return;
      const onDocClick = (e: MouseEvent) => {
        const target = e.target as Node;
        if (
          containerRef.current &&
          !containerRef.current.contains(target) &&
          !panelRef.current?.contains(target)
        ) {
          setOpen(false);
          setIsFocused(false);
        }
      };
      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }, [open, isFocused]);

    useEffect(() => {
      if (!open) return;
      const trigger = triggerRef.current;
      if (trigger) {
        panelSideRef.current = preferredSide(
          viewportBudgets(trigger.getBoundingClientRect())
        );
      }
      updatePanelPosition();
      // Focus the search input after the panel renders
      requestAnimationFrame(() => searchRef.current?.focus());
      window.addEventListener('scroll', updatePanelPosition, true);
      window.addEventListener('resize', updatePanelPosition);
      return () => {
        window.removeEventListener('scroll', updatePanelPosition, true);
        window.removeEventListener('resize', updatePanelPosition);
      };
    }, [open, updatePanelPosition]);

    const toggle = () => {
      if (disabled) return;
      const next = !open;
      setOpen(next);
      setIsFocused(true);
      if (next) {
        setQuery('');
        onFocus?.();
      }
    };

    const select = (opt: Option) => {
      onChange?.(name, opt.value);
      setOpen(false);
      setIsFocused(false);
      setQuery('');
    };

    const handleKeyDown = (e: ReactKeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
        setIsFocused(false);
        triggerRef.current?.focus();
      }
    };

    const renderOption = (opt: Option) => (
      <li
        key={opt.value || '__empty__'}
        role="option"
        aria-selected={opt.value === value}
        className={cn(
          'px-4 py-2 text-sm cursor-pointer hover:bg-neut-50',
          opt.value === value
            ? 'bg-prim-50 font-medium text-prim'
            : 'text-neut-prim'
        )}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        onClick={() => select(opt)}
      >
        {opt.label}
      </li>
    );

    return (
      <InputWrapper
        label={label}
        id={id}
        errorId={errorId}
        error={error}
        required={required}
        disabled={disabled}
        containerClassName={containerClassName}
        focus={isFocused || open}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
      >
        <div className="relative w-full" ref={containerRef}>
          <button
            type="button"
            ref={triggerRef}
            name={name}
            id={id}
            className={cn(
              'bg-none border-none focus:outline-none hover:outline-none py-3 px-4 text-sm leading-[100%] bg-neut-0 rounded font-normal w-full text-left',
              'flex items-center justify-between',
              className
            )}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={`${id}-panel`}
            aria-describedby={error ? errorId : undefined}
            aria-disabled={disabled}
            disabled={disabled}
            onClick={toggle}
            onFocus={() => setIsFocused(true)}
            onKeyDown={handleKeyDown}
            data-cy={dataCy}
          >
            <span className={cn(!value ? 'text-neut-400' : '')}>
              {selectedLabel ||
                placeholder ||
                t('searchable_select.placeholder')}
            </span>
            <svg
              className={cn(
                'shrink-0 ml-2 h-4 w-4 text-neut-500 transition-transform',
                open ? 'rotate-180' : undefined
              )}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.186l3.71-3.955a.75.75 0 111.08 1.04l-4.24 4.52a.75.75 0 01-1.08 0l-4.24-4.52a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {open &&
            createPortal(
              <div
                ref={panelRef}
                style={panelStyle}
                className={cn(
                  'z-[1100] flex flex-col overflow-hidden rounded border border-neut-border bg-white shadow-md',
                  panelClassName
                )}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={handleKeyDown}
              >
                <div className="shrink-0 px-2 py-2 border-b border-neut-border">
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    aria-label={
                      typeof label === 'string'
                        ? t('searchable_select.search_label', { label })
                        : t('searchable_select.search_options_label')
                    }
                    placeholder={t(
                      creatable
                        ? 'searchable_select.search_or_create_placeholder'
                        : 'searchable_select.search_placeholder'
                    )}
                    className="w-full px-3 py-1.5 text-sm rounded border border-neut-200 focus:outline-none focus:border-prim focus:ring-1 focus:ring-prim/50"
                  />
                </div>

                <div className="max-h-52 min-h-0 overflow-y-auto py-1">
                  <ul
                    id={`${id}-panel`}
                    role="listbox"
                    aria-label={typeof label === 'string' ? label : undefined}
                  >
                    {ungroupedOptions.map(renderOption)}
                    {groupedOptions.map((groupEntry) => (
                      <li
                        key={`__group__${groupEntry.group}`}
                        role="group"
                        aria-label={groupEntry.group}
                      >
                        <p className="px-4 pt-2 pb-1 text-[10px] font-semibold text-neut-500 uppercase tracking-wide">
                          {groupEntry.group}
                        </p>
                        <ul role="presentation">
                          {groupEntry.options.map(renderOption)}
                        </ul>
                      </li>
                    ))}
                    {showCreateOption && (
                      <li
                        role="option"
                        aria-selected={false}
                        className="px-4 py-2 text-sm cursor-pointer hover:bg-neut-50 text-prim font-medium"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() =>
                          select({ value: trimmedQuery, label: trimmedQuery })
                        }
                      >
                        {t('searchable_select.create_option', {
                          value: trimmedQuery,
                        })}
                      </li>
                    )}
                  </ul>
                  {filtered.length === 0 && !showCreateOption && (
                    <div className="px-3 py-2 text-sm text-neut-500">
                      {t('searchable_select.no_results')}
                    </div>
                  )}
                </div>
              </div>,
              panelContainer
            )}
        </div>
      </InputWrapper>
    );
  }
);

SearchableSelect.displayName = 'SearchableSelect';
